import { FiltroEmbed, MeusPaineisViewModel, PaginaRelatorio, RefreshStatus, RelatorioResumo } from "../../../interfaces/Paineis";

/**
 * Lógica pura dos Painéis (sem React e sem `powerbi-client`), para ser testada sem o SDK.
 * O iframe e os eventos do SDK ficam em `visualizador.tsx`.
 */

/** Antecedência da renovação: o token é trocado quando faltam 5 min para expirar. */
export const MARGEM_RENOVACAO_MS = 5 * 60_000;
/** Largura abaixo da qual a tela é tratada como celular. */
export const LIMITE_CELULAR_PX = 768;
export const SEM_AREA = "Sem área";

export function agruparPorArea(relatorios: RelatorioResumo[]): [string, RelatorioResumo[]][] {
  const mapa = new Map<string, RelatorioResumo[]>();
  relatorios.forEach((r) => {
    const area = r.area?.trim() || SEM_AREA;
    mapa.set(area, [...(mapa.get(area) ?? []), r]);
  });
  return Array.from(mapa.entries()).sort(([a], [b]) => a.localeCompare(b, "pt-BR"));
}

export type DestinoDaLista =
  | { tipo: "navegar"; id: number }
  | { tipo: "mensagem"; texto: string }
  | { tipo: "lista" };

/**
 * O que a tela "Meus painéis" faz com a resposta: abrir direto, avisar ou listar.
 *
 * `forcarLista` (`?lista=1`, usado pelo link "Meus painéis" do visualizador) desliga o atalho de
 * relatório único: sem isso, quem tem 1 só painel clica em "voltar" e é devolvido ao painel, num
 * laço sem saída.
 */
export function destinoDaLista(d: MeusPaineisViewModel, forcarLista = false): DestinoDaLista {
  if (!d.disponivel) {
    return {
      tipo: "mensagem",
      texto:
        d.motivo === "sem-organizacao"
          ? "Entre em uma organização para ver painéis."
          : "Painéis temporariamente indisponíveis.",
    };
  }
  if (d.relatorios.length === 1 && !forcarLista) return { tipo: "navegar", id: d.relatorios[0].id };
  if (d.relatorios.length === 0) {
    return {
      tipo: "mensagem",
      texto: "Ainda não há painéis liberados para você. Fale com o administrador da sua organização.",
    };
  }
  return { tipo: "lista" };
}

/** "há 5 min", "há 3 h", "há 2 dias" — a partir de uma data ISO. */
export function tempoDesde(iso: string, agora: number = Date.now()): string {
  const minutos = Math.max(0, Math.floor((agora - new Date(iso).getTime()) / 60_000));
  if (minutos < 1) return "agora há pouco";
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `há ${dias} ${dias === 1 ? "dia" : "dias"}`;
}

/** Milissegundos até a próxima renovação (5 min antes de expirar); nunca negativo. */
export function momentoDeRenovar(expiresOn: string, agora: number = Date.now()): number {
  return Math.max(0, new Date(expiresOn).getTime() - MARGEM_RENOVACAO_MS - agora);
}

export type ReacaoARenovacao = "semAcesso" | "indisponivel" | "retentar";

/** 404 = acesso revogado; 503 = conexão do cliente indisponível; o resto é transitório. */
export function reacaoARenovacao(status: number | undefined): ReacaoARenovacao {
  if (status === 404) return "semAcesso";
  if (status === 503) return "indisponivel";
  return "retentar";
}

export type LayoutCelular = "MobilePortrait" | "FitToWidth";

/** Layout do relatório no celular; `null` no desktop (não mexe em nada). */
export function layoutNoCelular(largura: number, temMobile: boolean): LayoutCelular | null {
  if (largura >= LIMITE_CELULAR_PX) return null;
  return temMobile ? "MobilePortrait" : "FitToWidth";
}

function formatarDataHora(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** Texto da faixa de aviso quando a última atualização falhou; `null` quando não há o que avisar. */
export function faixaDeFalha(status: RefreshStatus, ultimoDadoBomEm: string | null): string | null {
  if (status !== "Falha") return null;
  return `A última atualização falhou.${ultimoDadoBomEm ? ` Dados de ${formatarDataHora(ultimoDadoBomEm)}.` : ""}`;
}

/** Minúsculas e sem acento, para comparar texto digitado com nomes cadastrados. */
export function normalizarBusca(texto: string): string {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

/** Relatórios cujo nome ou área contém o termo (sem acento, sem diferenciar caixa). Termo vazio = todos. */
export function filtrarRelatorios(relatorios: RelatorioResumo[], termo: string): RelatorioResumo[] {
  const t = normalizarBusca(termo);
  if (!t) return relatorios;
  return relatorios.filter((r) => normalizarBusca(`${r.nome} ${r.area ?? ""}`).includes(t));
}

/** Valor de `models.FilterType.Basic` no SDK; fixo aqui para esta lógica não depender do `powerbi-client`. */
export const FILTRO_BASICO = 1;

export interface FiltroBasico {
  $schema: string;
  target: { table: string; column: string };
  operator: "In";
  values: string[];
  filterType: number;
}

/** Um `BasicFilter` (operador In) por filtro de dados da pessoa. */
export function montarFiltrosBasicos(filtros: FiltroEmbed[]): FiltroBasico[] {
  return filtros.map((f) => ({
    $schema: "http://powerbi.com/product/schema#basic",
    target: { table: f.tabela, column: f.coluna },
    operator: "In",
    values: f.valores,
    filterType: FILTRO_BASICO,
  }));
}

/** Aba aberta no início: a do `?aba=` se estiver liberada, senão a primeira; `undefined` sem abas. */
export function abaInicial(paginas: PaginaRelatorio[], abaDaUrl: string | null): string | undefined {
  if (abaDaUrl && paginas.some((p) => p.name === abaDaUrl)) return abaDaUrl;
  return paginas[0]?.name;
}

export type DecisaoDeAba = { tipo: "permitida" } | { tipo: "voltar"; para: string };

/** Aba fora das liberadas: volta para a primeira (e quem chama registra `AbaNaoPermitida`). */
export function decidirAba(paginas: PaginaRelatorio[], nomeDaAba: string | undefined): DecisaoDeAba {
  if (!nomeDaAba || paginas.length === 0 || paginas.some((p) => p.name === nomeDaAba)) return { tipo: "permitida" };
  return { tipo: "voltar", para: paginas[0].name };
}

/** Segundos inteiros entre dois instantes (ms). */
export function segundosEntre(inicio: number, fim: number): number {
  return Math.max(0, Math.floor((fim - inicio) / 1000));
}

/** Abaixo disto o tempo na tela não vale um evento. */
export const MIN_SEGUNDOS_TEMPO_NA_TELA = 5;

export const MOTIVO_MIN = 10;
export const MOTIVO_MAX = 1000;
export const NOME_VISAO_MAX = 120;
export const ESTADO_VISAO_MAX = 65_536;

/** Motivo do pedido de acesso: 10 a 1000 caracteres, sem contar espaços nas pontas. */
export function motivoValido(motivo: string): boolean {
  const n = motivo.trim().length;
  return n >= MOTIVO_MIN && n <= MOTIVO_MAX;
}

export function nomeDaVisaoValido(nome: string): boolean {
  const n = nome.trim().length;
  return n >= 1 && n <= NOME_VISAO_MAX;
}

/** O servidor recusa estado vazio ou acima de 65536 caracteres. */
export function estadoDaVisaoValido(estado: string | undefined | null): estado is string {
  return !!estado && estado.length <= ESTADO_VISAO_MAX;
}
